import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import {
  Activity,
  ArrowLeft,
  BookOpen,
  Brain,
  Check,
  ChevronRight,
  Cpu,
  FileText,
  Layers,
  Lock,
  Search,
  Settings,
  Shield,
  Terminal,
  Trophy,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePreview } from '../hooks/usePreview';
import { parcoursService } from '../services/parcoursService';
import { usePesaParcours } from '../stores/usePesaParcours';

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — identiques Landing & ParcoursList
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  bg:        '#050a0e',
  bgPanel:   '#080d12',
  bgCard:    '#0b1219',
  border:    '#1a2a38',
  accent:    '#e07b20',
  accentG:   '#f59e0b',
  cyan:      '#0ea5e9',
  cyanG:     '#38bdf8',
  green:     '#10b981',
  purple:    '#8b5cf6',
  red:       '#f87171',
  gold:      '#C9A55A',
  goldGlow:  'rgba(201,165,90,0.35)',
  text:      '#f7f7f7',
  textDim:   'rgba(250, 250, 250, 0.93)',
  textMute:  'rgba(255, 255, 255, 0.45)',
  scan:      'rgba(14,165,233,0.018)',
  // surfaces PESA sim
  surface0:  'rgba(5,10,14,0.97)',
  surface1:  'rgba(8,13,18,0.93)',
  surface2:  'rgba(11,18,25,0.85)',
  border0:   'rgba(26,42,56,0.7)',
  border1:   'rgba(26,42,56,1)',
};

// ─────────────────────────────────────────────────────────────────────────────
// PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
const Mono = ({ children, size = 11, color = T.textMute, w = false, style = {} }) => (
  <span style={{
    fontFamily: "'DM Mono', 'Roboto Mono', monospace",
    fontSize: size, letterSpacing: '0.06em',
    color, fontWeight: w ? 700 : 400, lineHeight: 1, ...style,
  }}>
    {children}
  </span>
);

const LiveClock = () => {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return <span>{time}</span>;
};

const HudCorners = ({ color1 = T.cyan, color2 = T.accent, size = 8 }) => (
  <>
    <span style={{ position: 'absolute', top: -1, left: -1, width: size, height: size, borderTop: `1px solid ${color1}`, borderLeft: `1px solid ${color1}`, zIndex: 5 }} />
    <span style={{ position: 'absolute', bottom: -1, right: -1, width: size, height: size, borderBottom: `1px solid ${color2}`, borderRight: `1px solid ${color2}`, zIndex: 5 }} />
  </>
);

const CardScanLine = ({ color = T.cyan }) => (
  <motion.div
    animate={{ top: ['-5%', '105%'], opacity: [0, 0.5, 0] }}
    transition={{ duration: 4.5, repeat: Infinity, ease: 'linear', repeatDelay: 3 }}
    style={{
      position: 'absolute', left: 0, right: 0, height: 1, pointerEvents: 'none', zIndex: 20,
      background: `linear-gradient(90deg, transparent, ${color}55, ${T.accentG}40, transparent)`,
    }}
  />
);

const CausalGrid = ({ activeColor }) => {
  const cols = 11, rows = 8;
  const nodes = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++)
      nodes.push({ id: `${r}-${c}`, x: (c / (cols - 1)) * 100, y: (r / (rows - 1)) * 100, delay: (r + c) * 0.06 });
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {Array.from({ length: rows }).map((_, r) => (
        <motion.div key={`h${r}`}
          animate={{ opacity: activeColor ? [0.04, 0.12, 0.04] : [0.015, 0.04, 0.015] }}
          transition={{ duration: 4 + r, repeat: Infinity, delay: r * 0.3 }}
          style={{ position: 'absolute', left: 0, right: 0, top: `${(r / (rows - 1)) * 100}%`, height: 1, background: `linear-gradient(90deg,transparent,${activeColor || T.cyan}40,transparent)` }}
        />
      ))}
      {Array.from({ length: cols }).map((_, c) => (
        <motion.div key={`v${c}`}
          animate={{ opacity: activeColor ? [0.04, 0.12, 0.04] : [0.015, 0.04, 0.015] }}
          transition={{ duration: 5 + c * 0.4, repeat: Infinity, delay: c * 0.25 }}
          style={{ position: 'absolute', top: 0, bottom: 0, left: `${(c / (cols - 1)) * 100}%`, width: 1, background: `linear-gradient(180deg,transparent,${activeColor || T.accent}30,transparent)` }}
        />
      ))}
      {nodes.map(n => (
        <motion.div key={n.id}
          animate={{ opacity: activeColor ? [0.12, 0.55, 0.12] : [0.02, 0.14, 0.02], scale: activeColor ? [0.8, 1.4, 0.8] : [0.8, 1, 0.8] }}
          transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: n.delay }}
          style={{ position: 'absolute', left: `${n.x}%`, top: `${n.y}%`, width: 3, height: 3, borderRadius: '50%', background: activeColor || T.cyan, transform: 'translate(-50%,-50%)' }}
        />
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LOADING
// ─────────────────────────────────────────────────────────────────────────────
const LoadingScreen = () => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: T.bg, gap: '2rem' }}>
    <div style={{ position: 'relative', width: 80, height: 80 }}>
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
        style={{ position: 'absolute', inset: 0, border: `1px solid ${T.cyan}18`, borderTop: `1px solid ${T.cyan}`, borderRight: `1px solid ${T.accent}55`, borderRadius: '50%' }} />
      <Cpu size={26} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: T.cyan }} strokeWidth={1} />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
      <Mono size={9} color={T.cyan} style={{ letterSpacing: '0.4em', textTransform: 'uppercase' }}>Chargement_Protocole...</Mono>
      <div style={{ width: 180, height: 1, background: 'rgba(255,255,255,0.04)', position: 'relative', overflow: 'hidden' }}>
        <motion.div animate={{ x: ['-100%', '100%'] }} transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, transparent, ${T.cyan}, ${T.accentG}, transparent)` }} />
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// HEXAGONE — tuile module
// ─────────────────────────────────────────────────────────────────────────────
const HEX_R   = 52;   // rayon pointy-top
const HEX_GAP = 10;
const COLS     = 5;

function hexPoints(cx, cy, r) {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(' ');
}

// Dimensions d'un hex pointy-top
const HEX_W = HEX_R * Math.sqrt(3);          // largeur
const HEX_H = HEX_R * 2;                     // hauteur
const COL_W  = HEX_W + HEX_GAP;
const ROW_H  = HEX_H * 0.75 + HEX_GAP;

function hexCenter(globalIdx) {
  const row = Math.floor(globalIdx / COLS);
  const col = globalIdx % COLS;
  const offsetX = row % 2 === 1 ? HEX_W / 2 : 0;
  return {
    x: col * COL_W + HEX_W / 2 + offsetX,
    y: row * ROW_H + HEX_H / 2,
  };
}

const HexTile = ({ mod, index, isSelected, onClick, dejaInscrit, tileRef }) => {
  const progression = mod?.progress || 0;
  const statut      = mod?.status   || 'not started';

  const accentColor =
    isSelected             ? T.accentG
    : statut === 'completed'   ? T.green
    : statut === 'in progress' ? T.accent
    : T.cyan;

  const fillOpacity  = isSelected ? 0.12 : 0.04;
  const strokeW      = isSelected ? 1.5  : 0.75;
  const strokeOpacity= isSelected ? 1    : 0.45;
  const perim        = 6 * HEX_R;

  const cx = HEX_W / 2, cy = HEX_H / 2;
  const pts = hexPoints(cx, cy, HEX_R);
  const labelWords = (mod?.titre || 'Module').replace(/[^a-zA-ZÀ-ÿ0-9 ]/g, '').trim().split(' ');
  const line1 = labelWords.slice(0, 2).join(' ');
  const line2 = labelWords.slice(2, 4).join(' ');

  return (
    <motion.div
      ref={tileRef}
      onClick={onClick}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 300, damping: 22 }}
      style={{ position: 'absolute', width: HEX_W, height: HEX_H, cursor: 'pointer', userSelect: 'none' }}
    >
      <svg width={HEX_W} height={HEX_H} viewBox={`0 0 ${HEX_W} ${HEX_H}`} style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
        <defs>
          <clipPath id={`hc${index}`}><polygon points={pts} /></clipPath>
          {/* Glow filter pour selected */}
          <filter id={`hg${index}`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="6" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* Fill */}
        <polygon points={pts} fill={accentColor} fillOpacity={fillOpacity} />

        {/* Scanlines internes */}
        <g clipPath={`url(#hc${index})`} opacity={0.4}>
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={i} x1={0} y1={i * (HEX_H / 10)} x2={HEX_W} y2={i * (HEX_H / 10)}
              stroke={T.cyan} strokeWidth={0.5} strokeOpacity={0.08} />
          ))}
        </g>

        {/* Progression arc */}
        {dejaInscrit && progression > 0 && (
          <polygon points={pts} fill="none" stroke={accentColor}
            strokeWidth={3} strokeOpacity={0.3}
            strokeDasharray={`${(progression / 100) * perim} ${perim}`} />
        )}

        {/* Contour principal */}
        <polygon points={pts} fill="none" stroke={accentColor}
          strokeWidth={strokeW} strokeOpacity={strokeOpacity}
          filter={isSelected ? `url(#hg${index})` : undefined} />

        {/* Halo sélectionné */}
        {isSelected && (
          <motion.polygon points={pts} fill="none" stroke={accentColor}
            strokeWidth={8} strokeOpacity={0.06}
            animate={{ strokeOpacity: [0.04, 0.1, 0.04] }}
            transition={{ duration: 2, repeat: Infinity }} />
        )}
      </svg>

      {/* Contenu texte */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 4, padding: '0 12px', pointerEvents: 'none',
      }}>
        <Terminal size={11} strokeWidth={1.5} style={{ color: accentColor, opacity: isSelected ? 1 : 0.7 }} />
        <span style={{
          fontFamily: "'DM Mono', monospace", fontSize: 8, fontWeight: isSelected ? 700 : 400,
          color: isSelected ? accentColor : T.textMute,
          textAlign: 'center', lineHeight: 1.5,
          textTransform: 'uppercase', letterSpacing: '0.1em',
        }}>
          {line1}
          {line2 && <><br />{line2}</>}
        </span>
        {dejaInscrit && progression > 0 && (
          <Mono size={9} color={accentColor} w style={{ marginTop: 1 }}>{progression}%</Mono>
        )}
        {statut === 'completed' && (
          <Check size={9} style={{ color: T.green }} strokeWidth={2.5} />
        )}
      </div>

      {/* Index */}
      <Mono size={7} color={`${T.textMute}`} style={{ position: 'absolute', top: '18%', right: '19%' }}>
        {String(index + 1).padStart(2, '0')}
      </Mono>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CONNECTEUR SVG — trait entre hex et carte
// ─────────────────────────────────────────────────────────────────────────────
const HexConnector = ({ fromX, fromY, toX, toY, color, visible }) => {
  if (!visible) return null;
  // Point de sortie bas du hex → point d'entrée haut de la carte
  const mx = (fromX + toX) / 2;
  const path = `M ${fromX} ${fromY} C ${fromX} ${fromY + 40}, ${toX} ${toY - 40}, ${toX} ${toY}`;

  return (
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 5, overflow: 'visible' }}>
      <defs>
        <filter id="conn-glow">
          <feGaussianBlur stdDeviation="3" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Glow */}
      <motion.path d={path} fill="none" stroke={color} strokeWidth={6} strokeOpacity={0.08}
        filter="url(#conn-glow)"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} />
      {/* Trait principal */}
      <motion.path d={path} fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.7}
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} />
      {/* Particule animée */}
      <motion.circle r={2.5} fill={color}
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
        style={{ offsetPath: `path('${path}')`, offsetDistance: '60%' }} />
      {/* Nœud d'arrivée */}
      <motion.circle cx={toX} cy={toY} r={4} fill={color} fillOpacity={0.15}
        stroke={color} strokeWidth={1} strokeOpacity={0.6}
        initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.35 }} />
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CARTE MENTALE MODULE — expandée sous la grille
// ─────────────────────────────────────────────────────────────────────────────
const CarteModule = ({ mod, onFermer, onNaviguer, dejaInscrit, color }) => {
  const [onglet, setOnglet] = useState('info');

  const statColor = mod?.status === 'completed' ? T.green
    : mod?.status === 'in progress' ? T.accent
    : color;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -12, scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 280, damping: 28 }}
      style={{
        position: 'relative',
        background: `linear-gradient(145deg, ${T.bgCard}, ${T.bgPanel})`,
        border: `1px solid ${statColor}32`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: `0 0 0 1px ${statColor}18, 0 8px 48px rgba(0,0,0,0.6), 0 0 60px ${statColor}08`,
      }}
    >
      {/* Accent top */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, ${statColor}60 30%, ${T.cyanG}40 70%, transparent)` }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents: 'none', borderRadius: 12 }} />
      <HudCorners color1={`${statColor}55`} color2={`${T.accent}55`} size={8} />
      <CardScanLine color={statColor} />

      {/* ── HEADER ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: `1px solid ${T.border}`, background: 'rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          {/* Indicateur statut */}
          <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}
            style={{ width: 8, height: 8, borderRadius: '50%', background: statColor, boxShadow: `0 0 10px ${statColor}`, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <Mono size={8} color={`${statColor}80`} style={{ letterSpacing: '0.28em', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
              Module_{String(mod._id?.slice(-4) || '0000').toUpperCase()}
            </Mono>
            <div style={{ fontFamily: "'Georgia', serif", fontWeight: 700, fontSize: '1rem', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
              {mod.titre}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          {/* Badge statut */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.2rem 0.55rem', border: `1px solid ${statColor}22`, background: `${statColor}08`, borderRadius: 4 }}>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: statColor }} />
            <Mono size={8} color={`${statColor}90`} style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              {mod.status === 'completed' ? 'Complété' : mod.status === 'in progress' ? 'En cours' : 'Non démarré'}
            </Mono>
          </div>
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={onFermer}
            style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMute, cursor: 'pointer' }}>
            <X size={12} />
          </motion.button>
        </div>
      </div>

      {/* ── ONGLETS ── */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, background: 'rgba(0,0,0,0.18)' }}>
        {[
          { id: 'info', label: 'Documentation', Icon: FileText },
          { id: 'res',  label: 'Ressources',    Icon: Layers   },
          { id: 'prog', label: 'Progression',   Icon: Activity },
        ].map(tab => {
          const a = onglet === tab.id;
          return (
            <button key={tab.id} onClick={() => setOnglet(tab.id)}
              style={{
                flex: 1, padding: '0.6rem 0', background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
                color: a ? statColor : T.textMute,
                borderBottom: a ? `2px solid ${statColor}` : '2px solid transparent',
                transition: 'all 0.2s',
              }}>
              <tab.Icon size={11} strokeWidth={1.5} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── CORPS EN COLONNES ── */}
      <AnimatePresence mode="wait">
        {onglet === 'info' && (
          <motion.div key="info"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1px', background: T.border }}>

            {/* Col 1 — Description */}
            <div style={{ background: T.bg, padding: '1rem 1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.7rem' }}>
                <FileText size={10} strokeWidth={1.5} style={{ color: `${T.cyan}60` }} />
                <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.24em', textTransform: 'uppercase' }}>Synopsis</Mono>
              </div>
              <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.42)', lineHeight: 1.75, fontStyle: 'italic', borderLeft: `2px solid ${T.cyan}25`, paddingLeft: '0.65rem' }}>
                {mod.description || 'Initialisation des vecteurs...'}
              </p>
            </div>

            {/* Col 2 — Objectifs */}
            <div style={{ background: T.bg, padding: '1rem 1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.7rem' }}>
                <Shield size={10} strokeWidth={1.5} style={{ color: `${T.cyan}60` }} />
                <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.24em', textTransform: 'uppercase' }}>Objectifs</Mono>
              </div>
              {mod.objectifs?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {mod.objectifs.slice(0, 4).map((obj, i) => (
                    <div key={i} style={{ display: 'flex', gap: 7, padding: '0.35rem 0.5rem', borderLeft: `2px solid ${T.accent}40`, background: `${T.accent}04`, borderRadius: '0 5px 5px 0' }}>
                      <Mono size={9} color={`${T.accent}70`} w style={{ flexShrink: 0 }}>0{i + 1}</Mono>
                      <Mono size={9} color="rgba(255,255,255,0.42)" style={{ lineHeight: 1.55 }}>{obj}</Mono>
                    </div>
                  ))}
                </div>
              ) : (
                <Mono size={9} color={T.textMute} style={{ fontStyle: 'italic' }}>Aucun objectif défini</Mono>
              )}
            </div>

            {/* Col 3 — Compétences */}
            <div style={{ background: T.bg, padding: '1rem 1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.7rem' }}>
                <Brain size={10} strokeWidth={1.5} style={{ color: `${T.cyan}60` }} />
                <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.24em', textTransform: 'uppercase' }}>Compétences</Mono>
              </div>
              {mod.competences?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                  {mod.competences.map((c, i) => (
                    <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.18rem 0.45rem', border: `1px solid ${T.cyan}22`, background: `${T.cyan}08`, borderRadius: 4 }}>
                      <span style={{ width: 3, height: 3, borderRadius: '50%', background: T.cyan }} />
                      <Mono size={8} color={`${T.cyan}90`} style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}>{c}</Mono>
                    </div>
                  ))}
                </div>
              ) : (
                <Mono size={9} color={T.textMute} style={{ fontStyle: 'italic' }}>Aucune compétence référencée</Mono>
              )}

              {/* Bonus fiches */}
              {mod.bonusFiches?.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.4rem' }}>
                    <BookOpen size={9} strokeWidth={1.5} style={{ color: `${T.accent}60` }} />
                    <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>Ressources bonus</Mono>
                  </div>
                  {mod.bonusFiches.slice(0, 2).map((f, i) => (
                    <div key={i} style={{ padding: '0.35rem 0.5rem', border: `1px solid ${T.border}`, background: T.bgCard, borderRadius: 6, marginBottom: '0.25rem' }}>
                      <Mono size={9} color={T.text} w style={{ display: 'block', marginBottom: 2 }}>{f.titre}</Mono>
                      <Mono size={8} color="rgba(255,255,255,0.32)" style={{ lineHeight: 1.5 }}>{f.description}</Mono>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {onglet === 'res' && (
          <motion.div key="res"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: T.border, minHeight: 180 }}>

            {/* Cas d'étude */}
            <div style={{ background: T.bg, padding: '1rem 1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.7rem' }}>
                <Trophy size={10} strokeWidth={1.5} style={{ color: `${T.accent}60` }} />
                <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.24em', textTransform: 'uppercase' }}>Études_de_Cas</Mono>
              </div>
              {mod.cases?.length > 0 ? mod.cases.map((c, i) => (
                <div key={i} style={{ padding: '0.55rem 0.7rem', border: `1px solid ${T.accent}22`, background: `${T.accent}05`, borderRadius: 7, marginBottom: '0.35rem', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = `${T.accent}45`; e.currentTarget.style.background = `${T.accent}09`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = `${T.accent}22`; e.currentTarget.style.background = `${T.accent}05`; }}>
                  <Mono size={9} color={T.text} w style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.caseId?.nom || 'Cas interactif'}</Mono>
                </div>
              )) : (
                <div style={{ padding: '1.5rem', textAlign: 'center', border: `1px dashed ${T.border}`, borderRadius: 8 }}>
                  <Lock size={14} strokeWidth={1} style={{ color: T.textMute, display: 'block', margin: '0 auto 6px' }} />
                  <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>Non_Disponible</Mono>
                </div>
              )}
            </div>

            {/* Granules */}
            <div style={{ background: T.bg, padding: '1rem 1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.7rem' }}>
                <Layers size={10} strokeWidth={1.5} style={{ color: `${T.cyan}60` }} />
                <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.24em', textTransform: 'uppercase' }}>Granules</Mono>
              </div>
              {mod.granules?.length > 0 ? mod.granules.slice(0, 5).map((g, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.4rem 0.55rem', border: `1px solid ${T.border}`, background: T.bgCard, borderRadius: 6, marginBottom: '0.3rem' }}>
                  <div style={{ width: 20, height: 20, borderRadius: 5, background: `${T.cyan}08`, border: `1px solid ${T.cyan}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Mono size={8} color={`${T.cyan}70`} w>{i + 1}</Mono>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <Mono size={9} color="rgba(255,255,255,0.55)" w style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', textTransform: 'uppercase' }}>{g.titre}</Mono>
                    <Mono size={8} color={T.textMute} style={{ textTransform: 'uppercase' }}>{g.type} · {g.duration}min</Mono>
                  </div>
                </div>
              )) : (
                <div style={{ padding: '1.5rem', textAlign: 'center', border: `1px dashed ${T.border}`, borderRadius: 8 }}>
                  <Lock size={14} strokeWidth={1} style={{ color: T.textMute, display: 'block', margin: '0 auto 6px' }} />
                  <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>Non_Disponible</Mono>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {onglet === 'prog' && (
          <motion.div key="prog"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1px', background: T.border, minHeight: 180 }}>

            {/* Global */}
            <div style={{ background: T.bg, padding: '1rem 1.1rem' }}>
              <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.24em', textTransform: 'uppercase', display: 'block', marginBottom: '0.7rem' }}>Progression_Globale</Mono>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: '0.5rem' }}>
                <span style={{ fontFamily: "'Georgia', serif", fontSize: '2.2rem', fontWeight: 700, color: statColor, lineHeight: 1 }}>{mod.progress || 0}</span>
                <Mono size={11} color={`${statColor}60`}>%</Mono>
              </div>
              <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: '0.85rem' }}>
                <motion.div initial={{ width: 0 }} animate={{ width: `${mod.progress || 0}%` }} transition={{ duration: 1.2, ease: 'easeOut' }}
                  style={{ height: '100%', background: `linear-gradient(90deg, ${T.cyan}, ${statColor})`, borderRadius: 2 }} />
              </div>
              {/* Barres théo/pratique/quiz */}
              {[
                { label: 'Théorique', val: mod.theoreticalGranules?.percentComplete || 0, color: T.cyan },
                { label: 'Pratique',  val: mod.practicalActivities?.percentComplete  || 0, color: T.accentG },
                { label: 'Quiz',      val: mod.finalQuiz?.bestScore                  || 0, color: T.green },
              ].map(({ label, val, color: c }) => (
                <div key={label} style={{ marginBottom: '0.45rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <Mono size={8} color={T.textMute}>{label}</Mono>
                    <Mono size={8} color={c}>{val}%</Mono>
                  </div>
                  <div style={{ height: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 1, overflow: 'hidden' }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${val}%` }} transition={{ duration: 1, ease: 'easeOut' }}
                      style={{ height: '100%', background: c, borderRadius: 1 }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Métriques */}
            <div style={{ background: T.bg, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.24em', textTransform: 'uppercase', display: 'block', marginBottom: '0.2rem' }}>Indicateurs</Mono>
              {[
                { k: 'Granules', v: mod.granules?.length || 0, u: 'unités' },
                { k: 'Cas',      v: mod.cases?.length   || 0, u: 'étude(s)' },
                { k: 'Compétences', v: mod.competences?.length || 0, u: 'acquises' },
                { k: 'Objectifs', v: mod.objectifs?.length || 0, u: 'définis' },
              ].map(({ k, v, u }) => (
                <div key={k} style={{ padding: '0.45rem 0.6rem', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 7 }}>
                  <Mono size={8} color={T.textMute} style={{ display: 'block', marginBottom: 2 }}>{k}</Mono>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 700, color: T.textDim }}>
                    {v}<span style={{ fontSize: 9, color: T.textMute, marginLeft: 4 }}>{u}</span>
                  </span>
                </div>
              ))}
            </div>

            {/* Temps + statut */}
            <div style={{ background: T.bg, padding: '1rem 1.1rem' }}>
              <Mono size={8} color={T.textMute} style={{ letterSpacing: '0.24em', textTransform: 'uppercase', display: 'block', marginBottom: '0.7rem' }}>Statut_Système</Mono>
              <div style={{ padding: '0.65rem 0.8rem', border: `1px solid ${statColor}22`, background: `${statColor}07`, borderRadius: 8, marginBottom: '0.5rem' }}>
                <Mono size={8} color={`${statColor}80`} style={{ display: 'block', marginBottom: 4 }}>État courant</Mono>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700, color: statColor, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  {mod.status === 'completed' ? '✓ Complété' : mod.status === 'in progress' ? '▶ En cours' : '○ Non démarré'}
                </div>
              </div>
              <div style={{ padding: '0.65rem 0.8rem', border: `1px solid ${T.border}`, background: T.bgCard, borderRadius: 8 }}>
                <Mono size={8} color={T.textMute} style={{ display: 'block', marginBottom: 4 }}>Architecture</Mono>
                <Mono size={9} color="rgba(255,255,255,0.32)" style={{ lineHeight: 1.6, display: 'block' }}>
                  Moteur causal découplé. Aucune dépendance tiers.
                </Mono>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FOOTER CTA ── */}
      <div style={{ padding: '0.85rem 1.25rem', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Mono size={8} style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}>PESACORE Architect·</Mono>
          <span style={{ color: T.border }}>·</span>
          <Mono size={8} color={`${T.green}70`} style={{ letterSpacing: '0.1em' }}>● NOMINAL</Mono>
        </div>
        <motion.button onClick={onNaviguer}
          whileHover={{ boxShadow: `0 0 24px ${statColor}35` }} whileTap={{ scale: 0.98 }}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.1rem',
            borderRadius: 8, background: `linear-gradient(135deg, ${statColor}22, ${statColor}10)`,
            border: `1px solid ${statColor}32`, color: statColor,
            fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700,
            letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer',
          }}>
          <Zap size={11} /> Démarrer le Module <ChevronRight size={11} />
        </motion.button>
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// GRILLE HEX + CONNECTEUR + CARTE — tout en un conteneur scrollable
// ─────────────────────────────────────────────────────────────────────────────
const GrilleAvecCarte = ({ modules, moduleSelectionne, onSelectModule, dejaInscrit }) => {
  const tileRefs    = useRef({});
  const gridRef     = useRef(null);
  const containerRef = useRef(null);

  const [connectorCoords, setConnectorCoords] = useState(null);

  // Calcul du connecteur SVG après layout
  useLayoutEffect(() => {
    if (!moduleSelectionne || !gridRef.current) { setConnectorCoords(null); return; }
    const idx = modules.findIndex(m => m._id === moduleSelectionne._id);
    if (idx < 0) { setConnectorCoords(null); return; }
    const tileEl = tileRefs.current[idx];
    if (!tileEl || !gridRef.current) { setConnectorCoords(null); return; }

    const gridRect = gridRef.current.getBoundingClientRect();
    const tileRect = tileEl.getBoundingClientRect();

    // Centre bas du hex
    const fromX = tileRect.left - gridRect.left + tileRect.width  / 2;
    const fromY = tileRect.top  - gridRect.top  + tileRect.height;
    // Centre haut de la carte (approximatif, 24px de marge)
    const toX   = gridRect.width / 2;
    const toY   = tileRect.bottom - gridRect.top + 24;

    setConnectorCoords({ fromX, fromY, toX, toY });
  }, [moduleSelectionne, modules]);

  if (!modules?.length) return null;

  // Dimensions grille
  const nRows  = Math.ceil(modules.length / COLS);
  const gridW  = COLS * COL_W + HEX_W / 2;
  const gridH  = HEX_H + (nRows - 1) * ROW_H;

  const selectedColor = moduleSelectionne
    ? moduleSelectionne.status === 'completed'   ? T.green
    : moduleSelectionne.status === 'in progress' ? T.accent
    : T.accentG
    : T.accentG;

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, width: '100%', maxWidth: 900 }}>

      {/* Grille hexagonale */}
      <div ref={gridRef} style={{ position: 'relative', width: gridW, height: gridH, flexShrink: 0, zIndex: 2 }}>
        {modules.map((mod, i) => {
          const { x, y } = hexCenter(i);
          return (
            <div key={mod._id || i} style={{ position: 'absolute', left: x - HEX_W / 2, top: y - HEX_H / 2 }}>
              <HexTile
                mod={mod} index={i}
                isSelected={moduleSelectionne?._id === mod._id}
                onClick={() => onSelectModule(mod)}
                dejaInscrit={dejaInscrit}
                tileRef={el => tileRefs.current[i] = el}
              />
            </div>
          );
        })}

        {/* SVG connecteur — positionné dans le repère de la grille, débordant vers le bas */}
        {connectorCoords && moduleSelectionne && (
          <div style={{ position: 'absolute', inset: 0, height: gridH + 200, pointerEvents: 'none' }}>
            <HexConnector
              fromX={connectorCoords.fromX}
              fromY={connectorCoords.fromY}
              toX={connectorCoords.toX}
              toY={connectorCoords.toY}
              color={selectedColor}
              visible={!!moduleSelectionne}
            />
          </div>
        )}
      </div>

      {/* Carte mentale expandée */}
      <div style={{ width: '100%', paddingTop: '1.5rem' }}>
        <AnimatePresence>
          {moduleSelectionne && (
            <CarteModule
              mod={moduleSelectionne}
              onFermer={() => onSelectModule(moduleSelectionne)}
              onNaviguer={() => {/* handled by parent */}}
              dejaInscrit={dejaInscrit}
              color={selectedColor}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SIDEBAR GAUCHE — carte parcours allégée
// ─────────────────────────────────────────────────────────────────────────────
const CarteParcours = ({ parcours, dejaInscrit, isPreview, onInscription, onLancer, user, slug, navigate }) => {
  if (!parcours) return null;
  const color = T.accentG;
  const difficulte = parcours.difficulte || 3;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      <div style={{
        position: 'relative', background: T.bgCard, border: `1px solid ${T.border}`,
        borderRadius: 10, overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents: 'none' }} />
        <CardScanLine color={color} />
        <HudCorners color1={`${T.cyan}55`} color2={T.accent} size={7} />

        {/* Header visuel */}
        <div style={{ height: 80, background: '#070d12', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Cpu size={30} strokeWidth={0.6} style={{ color: `${T.cyan}10` }} />
          <div style={{ position: 'absolute', top: '0.4rem', right: '0.4rem' }}>
            <Mono size={7} color={`${T.cyan}70`} style={{ padding: '0.15rem 0.4rem', background: 'rgba(5,10,14,0.8)', border: `1px solid rgba(255,255,255,0.08)`, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              SEC_L01
            </Mono>
          </div>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, ${T.cyan}28, ${T.accent}22, transparent)` }} />
        </div>

        <div style={{ padding: '0.85rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
            <Mono size={8} color={`${T.cyan}60`} style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>
              Ref: {parcours.slug?.toUpperCase() || '—'}
            </Mono>
            <Settings size={10} strokeWidth={1} style={{ color: T.textMute, animation: 'spin 8s linear infinite' }} />
          </div>

          <div style={{ fontFamily: "'Georgia', serif", fontWeight: 700, fontSize: '0.92rem', color: T.text, lineHeight: 1.2, marginBottom: '0.45rem', letterSpacing: '-0.01em' }}>
            {parcours.nom}
          </div>

          <Mono size={9} color="rgba(255,255,255,0.35)" style={{ lineHeight: 1.65, display: 'block', marginBottom: '0.7rem',
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
            {parcours.description || 'En attente de documentation...'}
          </Mono>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem', marginBottom: '0.7rem' }}>
            <div style={{ background: '#070d13', borderRadius: 6, padding: '0.28rem 0.4rem', border: `1px solid ${T.border}` }}>
              <Mono size={7} color={T.textMute} style={{ display: 'block', marginBottom: '0.18rem' }}>Difficulté</Mono>
              <div style={{ display: 'flex', gap: 2 }}>
                {[1, 2, 3, 4, 5].map(v => (
                  <div key={v} style={{ width: 9, height: 2, borderRadius: 1, background: v <= difficulte ? `linear-gradient(90deg, ${T.cyan}, ${color})` : 'rgba(255,255,255,0.06)' }} />
                ))}
              </div>
            </div>
            <div style={{ background: '#070d13', borderRadius: 6, padding: '0.28rem 0.4rem', border: `1px solid ${T.border}` }}>
              <Mono size={7} color={T.textMute} style={{ display: 'block', marginBottom: '0.18rem' }}>Modules</Mono>
              <Mono size={15} color={T.cyan} w>{parcours.modules?.length || 0}</Mono>
            </div>
          </div>

          <motion.button
            onClick={dejaInscrit ? onLancer : onInscription}
            whileHover={{ boxShadow: dejaInscrit ? `0 0 18px ${T.cyan}25` : `0 0 18px ${color}28` }}
            whileTap={{ scale: 0.98 }}
            style={{
              width: '100%', padding: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: dejaInscrit ? `${T.cyan}0d` : `${color}0d`,
              border: `1px solid ${dejaInscrit ? T.cyan + '32' : color + '32'}`,
              color: dejaInscrit ? T.cyanG : color,
              fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: 7,
            }}>
            <span>{isPreview ? 'Test_Preview' : dejaInscrit ? 'Démarrer le parcours' : 'Initialisation du Protocole'}</span>
            {dejaInscrit ? <Terminal size={11} strokeWidth={1.5} /> : <ChevronRight size={11} strokeWidth={1.5} />}
          </motion.button>
        </div>
      </div>

      {/* Badges */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
        {[
          { label: 'Core: Stable', color: T.green },
          { label: `Agent: ${user?.nom || 'Guest'}`, color: T.cyan },
          ...(dejaInscrit ? [{ label: 'Enrolled', color: T.accentG }] : []),
        ].map(b => (
          <div key={b.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.2rem 0.5rem', border: `1px solid ${b.color}22`, background: `${b.color}08`, borderRadius: 4 }}>
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: b.color, animation: 'dotpulse 2s ease-in-out infinite' }} />
            <Mono size={8} color={`${b.color}90`} style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}>{b.label}</Mono>
          </div>
        ))}
      </div>

      {/* Mini-progression */}
      {parcours.modules && dejaInscrit && (
        <div style={{ padding: '0.75rem', border: `1px solid ${T.border}`, background: T.bgCard, borderRadius: 8, position: 'relative' }}>
          <HudCorners color1={`${T.cyan}35`} color2={`${T.accent}35`} size={5} />
          <Mono size={7} color={T.textMute} style={{ letterSpacing: '0.28em', textTransform: 'uppercase', display: 'block', marginBottom: '0.6rem' }}>Progression_Globale</Mono>
          {parcours.modules.map((mod, i) => {
            const prog   = mod.progress || 0;
            const statut = mod.status || 'not started';
            const c = statut === 'completed' ? T.green : statut === 'in progress' ? T.accent : `${T.cyan}35`;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem' }}>
                <Mono size={7} color={T.textMute} w style={{ flexShrink: 0, width: 16 }}>{String(i + 1).padStart(2, '0')}</Mono>
                <div style={{ flex: 1, height: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', borderRadius: 1 }}>
                  <div style={{ height: '100%', width: `${prog}%`, background: c, transition: 'width 0.8s ease', borderRadius: 1 }} />
                </div>
                <Mono size={8} color={c} w style={{ flexShrink: 0, width: 24, textAlign: 'right' }}>{prog}%</Mono>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MODALE INSCRIPTION
// ─────────────────────────────────────────────────────────────────────────────
const ModaleInscription = ({ parcours, onConfirmer, onFermer }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    style={{ position: 'fixed', inset: 0, zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', background: 'rgba(5,10,14,0.92)', backdropFilter: 'blur(12px)' }}
    onClick={onFermer}>
    <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
      onClick={e => e.stopPropagation()}
      style={{ width: '100%', maxWidth: 440, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', position: 'relative', boxShadow: `0 0 0 1px ${T.accentG}18, 0 24px 64px rgba(0,0,0,0.8)` }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, ${T.cyan}50 30%, ${T.accentG}40 70%, transparent)` }} />
      <HudCorners color1={T.cyan} color2={T.accent} size={8} />

      <div style={{ padding: '0.9rem 1.2rem', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Zap size={13} strokeWidth={1.5} style={{ color: T.accentG }} />
          <Mono size={10} color={T.text} w style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>Accréditation_Requise</Mono>
        </div>
        <button onClick={onFermer} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMute, display: 'flex' }}>
          <X size={15} />
        </button>
      </div>

      <div style={{ padding: '1.4rem 1.2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ padding: '0.7rem 0.85rem', border: `1px solid ${T.cyan}14`, background: `${T.cyan}04`, borderLeft: `2px solid ${T.cyan}40`, borderRadius: '0 8px 8px 0' }}>
          <Mono size={10} color="rgba(255,255,255,0.48)" style={{ fontStyle: 'italic', lineHeight: 1.8, display: 'block' }}>
            Accès au protocole <span style={{ color: T.cyanG, fontStyle: 'normal', fontWeight: 700 }}>[{parcours?.nom || '—'}]</span>
          </Mono>
        </div>
        <div style={{ textAlign: 'center', fontFamily: "'Georgia', serif", fontSize: '0.8rem', color: T.textMute, lineHeight: 1.8 }}>
          Allocation de <span style={{ color: T.accentG, fontWeight: 700 }}>220 PESAS</span> détectée.<br />Confirmez-vous l'initialisation ?
        </div>
        <motion.button onClick={onConfirmer}
          whileHover={{ boxShadow: `0 0 28px ${T.accentG}35` }} whileTap={{ scale: 0.98 }}
          style={{ width: '100%', padding: '0.85rem', background: `linear-gradient(135deg, ${T.accentG}, #fbbf24)`, border: 'none', color: T.bg, fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: 8 }}>
          Confirm_System_Access
        </motion.button>
        <button onClick={onFermer}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: T.textMute }}>
          Annuler
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
const ParcoursDetails = () => {
  const { user }   = useAuth();
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const { scrollY } = useScroll();

  const [parcoursActuel,    setParcoursActuel]    = useState(null);
  const [chargement,        setChargement]        = useState(true);
  const [dejaInscrit,       setDejaInscrit]       = useState(false);
  const [moduleSelectionne, setModuleSelectionne] = useState(null);
  const [modaleActive,      setModaleActive]      = useState(false);
  const [scrolled,          setScrolled]          = useState(false);
  const [searchTerm,        setSearchTerm]        = useState('');

  const { initParcours, focusResource } = usePesaParcours();
  const { isPreview, previewData }      = usePreview();

  useMotionValueEvent(scrollY, 'change', y => setScrolled(y > 40));

  useEffect(() => {
    const charger = async () => {
      if (isPreview && previewData) {
        setParcoursActuel(previewData); setDejaInscrit(true); setChargement(false); return;
      }
      try {
        setChargement(true);
        const { course, progress, alreadyEnrolled } = await parcoursService.getInvestigation(slug);
        setParcoursActuel(course);
        setDejaInscrit(alreadyEnrolled || !!progress);
      } catch {
        toast.error('Protocole introuvable');
        navigate('/parcours');
      } finally {
        setChargement(false);
      }
    };
    charger();
  }, [slug, navigate, isPreview, previewData]);

  const lancerInscription = async () => {
    try {
      const { course, progress } = await parcoursService.enroll(slug);
      initParcours(course, progress);
      toast.success('Protocole initialisé avec succès');
      navigate(`/investigation/${slug}`);
    } catch (err) {
      toast.error(`Erreur : ${err.message || 'Inconnue'}`);
    }
  };

  const selectionnerModule = (mod) => {
    const isDeselect = moduleSelectionne?._id === mod._id;
    setModuleSelectionne(isDeselect ? null : mod);
    if (!isDeselect) {
      const idGranule = mod.granules?.[0]?.id;
      if (idGranule) focusResource(idGranule);
    }
  };

  const modulesFiltres = parcoursActuel?.modules?.filter(m =>
    !searchTerm || m.titre?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const activeColor = moduleSelectionne
    ? moduleSelectionne.status === 'completed'   ? T.green
    : moduleSelectionne.status === 'in progress' ? T.accent
    : T.accentG
    : null;

  if (chargement) return <LoadingScreen />;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: T.bg, fontFamily: "'DM Mono', monospace", overflow: 'hidden', position: 'relative', color: T.text }}>

      {/* Fond */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <CausalGrid activeColor={activeColor} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`, backgroundSize: '52px 52px', opacity: 0.25 }} />
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 20% 0%, ${T.cyan}06 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, ${T.accent}05 0%, transparent 50%)` }} />
      </div>

      {/* ── TOP RAIL ── */}
      <motion.header animate={{ opacity: scrolled ? 0.96 : 1 }}
        style={{ position: 'relative', zIndex: 40, background: `${T.bgPanel}d8`, backdropFilter: 'blur(20px)', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0.55rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/parcours')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: T.textMute, padding: 0, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = T.textDim}
            onMouseLeave={e => e.currentTarget.style.color = T.textMute}>
            <ArrowLeft size={13} />
            <Mono size={10} color="inherit">Parcours</Mono>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Terminal size={13} style={{ color: T.cyan }} strokeWidth={1.5} />
            <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.3em', textTransform: 'uppercase' }}>
              {parcoursActuel?.nom ? parcoursActuel.nom.substring(0, 30) : 'Parcours.Engine'}
            </Mono>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
            <Mono size={9} color={`${T.accentG}80`}><LiveClock /></Mono>
            <span style={{ width: 1, height: 12, background: T.border }} />
            <Mono size={9} color={`${T.green}80`} style={{ letterSpacing: '0.2em' }}>● SYSTEM STABLE</Mono>
          </div>
        </div>
      </motion.header>

      {/* ── CORPS ── */}
      <main className="pd-main" style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative', zIndex: 10 }}>

        {/* Sidebar gauche */}
        <aside className="pd-sidebar" style={{ width: 268, flexShrink: 0, padding: '1.1rem 0.9rem', overflowY: 'auto', borderRight: `1px solid ${T.border}`, background: `${T.bgPanel}85`, backdropFilter: 'blur(12px)' }}>
          <motion.div initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.38 }}>
            <CarteParcours
              parcours={parcoursActuel} dejaInscrit={dejaInscrit} isPreview={isPreview}
              onInscription={() => setModaleActive(true)}
              onLancer={() => navigate(`/investigation/${slug}`)}
              user={user} slug={slug} navigate={navigate}
            />
          </motion.div>
        </aside>

        {/* Zone centrale scrollable */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Barre contexte */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', padding: '0.55rem 1.25rem', borderBottom: `1px solid ${T.border}`, background: `${T.bgPanel}90`, backdropFilter: 'blur(16px)', flexShrink: 0, position: 'relative' }}>
            <HudCorners color1={`${T.cyan}35`} color2={`${T.accent}35`} size={5} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Layers size={10} strokeWidth={1.5} style={{ color: `${T.cyan}55` }} />
              <Mono size={9} color={`${T.cyan}55`} style={{ letterSpacing: '0.28em', textTransform: 'uppercase' }}>Carte_Modules</Mono>
            </div>
            <span style={{ color: T.border }}>·</span>
            <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              {modulesFiltres.length}/{parcoursActuel?.modules?.length || 0} modules
            </Mono>
            {moduleSelectionne && (
              <>
                <span style={{ color: T.border }}>·</span>
                <motion.div initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
                  style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: activeColor, boxShadow: `0 0 8px ${activeColor}` }} />
                  <Mono size={9} color={`${activeColor}90`} style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    {moduleSelectionne.titre?.substring(0, 28)}
                  </Mono>
                </motion.div>
              </>
            )}
            <div style={{ position: 'relative', flex: '1 1 140px', maxWidth: 240, marginLeft: 'auto' }}>
              <Search size={10} style={{ position: 'absolute', left: '0.55rem', top: '50%', transform: 'translateY(-50%)', color: `${T.cyan}50`, pointerEvents: 'none' }} />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="FILTRER..."
                style={{ width: '100%', background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}`, outline: 'none', padding: '0.38rem 0.55rem 0.38rem 1.7rem', fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: '0.15em', color: T.text, caretColor: T.cyan, borderRadius: 4, transition: 'border-color 0.2s' }}
                onFocus={e => e.target.style.borderColor = `${T.cyan}40`}
                onBlur={e => e.target.style.borderColor = T.border} />
            </div>
          </div>

          {/* Scroll principal */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '2rem 2rem 3rem', display: 'flex', justifyContent: 'center' }}>
            {modulesFiltres.length > 0 ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35, delay: 0.1 }}
                style={{ width: '100%', maxWidth: 900 }}>
                <GrilleAvecCarte
                  modules={modulesFiltres}
                  moduleSelectionne={moduleSelectionne}
                  onSelectModule={selectionnerModule}
                  dejaInscrit={dejaInscrit}
                />
              </motion.div>
            ) : (
              <div style={{ textAlign: 'center', paddingTop: 80, position: 'relative' }}>
                <HudCorners color1={`${T.cyan}30`} color2={`${T.accent}30`} size={8} />
                <Lock size={22} strokeWidth={1} style={{ color: T.textMute, display: 'block', margin: '0 auto 12px' }} />
                <Mono size={10} color={T.textMute} style={{ letterSpacing: '0.25em', textTransform: 'uppercase' }}>
                  {searchTerm ? 'Aucun_Module_Trouvé' : 'Aucun_Module_Disponible'}
                </Mono>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ── MODALE INSCRIPTION ── */}
      <AnimatePresence>
        {modaleActive && (
          <ModaleInscription parcours={parcoursActuel} onConfirmer={lancerInscription} onFermer={() => setModaleActive(false)} />
        )}
      </AnimatePresence>

      {/* ── FOOTER RAIL ── */}
      <footer style={{ position: 'relative', zIndex: 50, height: 40, background: `${T.bgPanel}e8`, backdropFilter: 'blur(20px)', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1.5rem', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: T.green, boxShadow: `0 0 6px ${T.green}` }} />
            <Mono size={8} color={`${T.green}70`} style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>Node_Online</Mono>
          </div>
          <Mono size={8} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            Total_Modules: <span style={{ color: T.cyan, fontWeight: 700 }}>{parcoursActuel?.modules?.length || 0}</span>
          </Mono>
          {moduleSelectionne && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Mono size={8} color={`${activeColor}70`} style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                Focus: {moduleSelectionne.titre?.substring(0, 22)}
              </Mono>
            </motion.div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
          <Mono size={8} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>Latency: 24ms</Mono>
          {[['SIM', T.green], ['CAU', T.cyan], ['INV', T.accent]].map(([tag, col]) => (
            <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <div style={{ width: 4, height: 4, borderRadius: '50%', background: col, boxShadow: `0 0 5px ${col}` }} />
              <Mono size={8} style={{ letterSpacing: '0.14em' }}>{tag}</Mono>
            </div>
          ))}
          <div style={{ padding: '0.18rem 0.65rem', background: `${T.cyan}0d`, border: `1px solid ${T.cyan}28` }}>
            <Mono size={8} color={`${T.cyan}70`} style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}>System_Authenticated</Mono>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes dotpulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${T.cyan}28; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: ${T.cyan}55; }
        @media (max-width: 767px) {
          .pd-main { flex-direction: column !important; overflow-y: auto !important; }
          .pd-sidebar { width: 100% !important; border-right: none !important; border-bottom: 1px solid ${T.border} !important; overflow-y: visible !important; }
        }
      `}</style>
    </div>
  );
};

export default ParcoursDetails;